import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Server, Database, Activity } from 'lucide-react';

const CapacityVisuals = ({ isDarkMode }) => {
  const stats = [
    { label: 'Compute Nodes', val: '40+', icon: <Cpu size={14}/> },
    { label: 'NVMe Storage', val: '2.5+ PB', icon: <Database size={14}/> },
    { label: 'Backplane Capacity', val: '240 Gbps', icon: <Activity size={14}/> },
  ];

  const zones = [
    { name: 'KATHMANDU', cpu: 25, storage: 28, ram: 18, color: 'emerald' },
    { name: 'LALITPUR', cpu: 18, storage: 22, ram: 11, color: 'amber' },
    { name: 'BUTWAL', cpu: 12, storage: 11, ram: 10, color: 'blue' },
  ];

  return (
    <div className="mt-20">
      <div className="text-left mb-12">
        <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Infrastructure Scale</h2>
        <p className={`max-w-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          Massive compute and storage resources deployed locally to empower Nepal's digital transformation.
        </p>
      </div>

      {/* Grid: Global Stats & Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Key Scale Stats */}
        <div className="lg:col-span-1 space-y-4">
          {stats.map((s, i) => (
            <div key={i} className={`p-6 rounded-2xl border transition-all ${isDarkMode ? 'bg-slate-900/40 border-slate-800 hover:border-slate-700' : 'bg-white border-slate-100 hover:shadow-md'}`}>
               <div className="text-emerald-500 mb-2">{s.icon}</div>
               <div className={`text-[10px] uppercase font-bold tracking-widest ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{s.label}</div>
               <div className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Resource Distribution Heatmap */}
        <div className={`lg:col-span-3 p-8 rounded-2xl border ${isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-white border-slate-200'} shadow-inner`}>
          <div className="flex items-center justify-between mb-8">
             <h4 className={`text-sm font-bold uppercase tracking-wider flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Server size={16} className="text-emerald-500" />
                Resource Heatmap by Zone
             </h4>
             <div className={`flex gap-4 text-[10px] font-bold ${isDarkMode ? 'opacity-50' : 'opacity-70'}`}>
                <div className="flex items-center gap-1"><div className="w-2 h-2 bg-emerald-500 rounded-sm"/> Compute</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 bg-blue-500 rounded-sm"/> Storage</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 bg-amber-500 rounded-sm"/> Memory</div>
             </div>
          </div>

          <div className="space-y-10">
             {zones.map((zone, i) => (
               <div key={i} className="relative">
                  <div className="flex justify-between items-center mb-3">
                     <span className={`text-xs font-black tracking-widest ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{zone.name}</span>
                     <span className={`text-[10px] font-mono uppercase ${isDarkMode ? 'opacity-40' : 'opacity-50'}`}>Cluster AZ-{i + 1}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                     {/* CPU Bar */}
                     <div className="space-y-1">
                        <div className={`flex justify-between text-[9px] font-bold ${isDarkMode ? 'opacity-60' : 'opacity-70'}`}><span>CPU UTILIZATION</span><span>{zone.cpu}%</span></div>
                        <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                           <motion.div initial={{width:0}} whileInView={{width:`${zone.cpu}%`}} transition={{duration: 1, delay: i * 0.2}} className="h-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]" />
                        </div>
                     </div>
                     {/* Storage Bar */}
                     <div className="space-y-1">
                        <div className={`flex justify-between text-[9px] font-bold ${isDarkMode ? 'opacity-60' : 'opacity-70'}`}><span>STORAGE ALLOCATED</span><span>{zone.storage}%</span></div>
                        <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                           <motion.div initial={{width:0}} whileInView={{width:`${zone.storage}%`}} transition={{duration: 1, delay: i * 0.2}} className="h-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.3)]" />
                        </div>
                     </div>
                     {/* RAM Bar */}
                     <div className="space-y-1">
                        <div className={`flex justify-between text-[9px] font-bold ${isDarkMode ? 'opacity-60' : 'opacity-70'}`}><span>RAM RESERVED</span><span>{zone.ram}%</span></div>
                        <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                           <motion.div initial={{width:0}} whileInView={{width:`${zone.ram}%`}} transition={{duration: 1, delay: i * 0.2}} className="h-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.3)]" />
                        </div>
                     </div>
                  </div>
               </div>
             ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapacityVisuals;
